import React, { useState } from 'react';
import { Sparkles, RefreshCw, CheckCircle, Download, ExternalLink, LogIn } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import StrategicResultsRenderer from '../shared/StrategicResultsRenderer';

const Step7_FinalAssessmentPreview = ({ wizardData, updateData, currentUser }) => {
  const { profile, finalAssessment, selectedAccount } = wizardData;
  const [generating, setGenerating] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);

  const generateFinalAssessment = async () => {
    setGenerating(true);

    try {
      console.log('🎯 Generating final strategic assessment using EXACT student flow...');

      const userId = selectedAccount.auth_user_id || selectedAccount.id;

      // Combine all responses (q1-q15)
      const allResponses = {
        ...wizardData.initialAnswers,
        ...wizardData.followupAnswers
      };

      // Use the EXACT same two-step API process that students go through
      // Step 1: Generate the strategic insights
      console.log('📡 Step 1: Calling generate-conversational-insights API...');
      const insightsResponse = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'generate-conversational-insights',
          responses: allResponses,
          strategicContext: wizardData.strategicContext?.summary || '',
          synthesisInstructions: wizardData.strategicContext?.summary || '',
          initialQuestions: wizardData.questionSet || [],
          isEnhancedAssessment: true, // CRITICAL: Tell API to use enhanced path
          studentProfile: {
            name: profile.name,
            graduationYear: profile.graduation_year,
            intendedMajor: profile.intended_major,
            careerGoals: profile.career_goals,
            gpa: profile.gpa_weighted || profile.gpa_unweighted,
            testScores: {
              sat: profile.sat_total,
              act: profile.act_composite
            },
            activities: profile.extracurriculars || []
          },
          parentContext: wizardData.parentInsights || {},
          userId: userId,
          studentName: profile.name
        })
      });

      if (!insightsResponse.ok) {
        const errorText = await insightsResponse.text();
        throw new Error(`Insights generation failed (${insightsResponse.status}): ${errorText}`);
      }

      const insightsResult = await insightsResponse.json();
      const rawInsights = insightsResult.insights || insightsResult;
      console.log('✅ Step 1 complete: Strategic insights generated');

      // Step 2: Format the insights for display
      console.log('📡 Step 2: Formatting insights with format-insights API...');
      const formatResponse = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'format-insights',
          rawText: typeof rawInsights === 'string' ? rawInsights : JSON.stringify(rawInsights),
          studentName: profile.name,
          studentSpecificConsiderations: wizardData.strategicContext?.summary || '',
          personalityProfile: profile.intended_major || '',
          synthesisGuidance: wizardData.strategicContext?.summary || '',
          // Request structured format like students get
          formattingInstructions: `Create a comprehensive strategic assessment with the following structured sections:
1. Strategic Advantage (opening statement)
2. Intellectual Signature (how their brain works)
3. Hidden Superpowers (unique strengths)
4. Strategic Positioning (how to stand out)
5. Essay Strategy (narrative recommendations)
6. College Recommendations (specific schools)

Format as structured JSON with these exact field names.`,
          sectionTitles: {
            strategicAdvantage: 'Your Strategic Advantage',
            intellectualSignature: 'How Your Brain Works',
            hiddenSuperpowers: 'Your Secret Weapons',
            strategicPositioning: 'How to Stand Out',
            essayStrategy: 'Essay Game Plan',
            collegeRecommendations: 'College Matches'
          },
          tonePreferences: 'insightful, strategic, and empowering',
          metaphorStyle: 'direct and actionable',
          studentProfile: {
            name: profile.name,
            intendedMajor: profile.intended_major,
            careerGoals: profile.career_goals
          }
        })
      });

      if (!formatResponse.ok) {
        const errorText = await formatResponse.text();
        throw new Error(`Formatting failed (${formatResponse.status}): ${errorText}`);
      }

      const formatResult = await formatResponse.json();
      const formattedInsights = formatResult.results || formatResult.formattedContent || rawInsights;
      console.log('✅ Step 2 complete: Insights formatted', {
        type: typeof formattedInsights,
        isObject: typeof formattedInsights === 'object',
        hasStrategicAdvantage: formattedInsights?.strategicAdvantage ? 'yes' : 'no'
      });

      // Keep the structured object for display, convert to string only for legacy fields
      const assessment = formattedInsights;
      const assessmentString = typeof formattedInsights === 'string' ? formattedInsights : JSON.stringify(formattedInsights, null, 2);

      // Save to database if we have a selected account
      if (selectedAccount) {
        const userId = selectedAccount.auth_user_id || selectedAccount.id;

        // Save the final assessment to student_academic_profiles (as string for legacy field)
        const { error: updateError } = await supabase
          .from('student_academic_profiles')
          .update({
            ai_strategic_summary: assessmentString,
            ai_strategic_summary_updated_at: new Date().toISOString()
          })
          .eq('user_id', userId);

        if (updateError) {
          console.error('Error saving assessment:', updateError);
        } else {
          console.log('✅ Assessment saved to database');
        }

        // Save all Q&A responses to assessment_responses table
        try {
          // Combine initial answers (1-8) and followup answers (9-15)
          const allResponses = {
            ...wizardData.initialAnswers,
            ...wizardData.followupAnswers
          };

          console.log('📊 Preparing to save assessment_responses:', {
            userId,
            responseCount: Object.keys(allResponses).length,
            initialQuestionCount: wizardData.questionSet?.length || 0,
            followupQuestionCount: wizardData.followupQuestions?.length || 0,
            hasInitialAnswers: !!wizardData.initialAnswers,
            hasFollowupAnswers: !!wizardData.followupAnswers,
            allResponses
          });

          // Check if assessment_responses record exists
          const { data: existingResponse, error: checkError } = await supabase
            .from('assessment_responses')
            .select('id')
            .eq('user_id', userId)
            .single();

          console.log('🔍 Existing response check:', { existingResponse, checkError });

          // Generate unique permalink for the results
          const timestamp = Date.now();
          const randomStr = Math.random().toString(36).substring(2, 8);
          const permalink = `${profile.name?.toLowerCase().replace(/\s+/g, '-') || 'student'}-${timestamp}-${randomStr}`;

          const assessmentData = {
            user_id: userId,
            assessment_type: 'enhanced',
            responses: allResponses,
            initial_questions: wizardData.questionSet || [],
            dynamic_questions: wizardData.followupQuestions || [],
            strategic_context: wizardData.strategicContext?.summary || null,
            assessment_phase: 'completed',
            completed_at: new Date().toISOString(),
            insights: rawInsights, // Save raw insights from Step 1
            formatted_insights: formattedInsights, // Save formatted insights from Step 2
            results_permalink: permalink,
            updated_at: new Date().toISOString()
          };

          console.log('💾 Assessment data to save:', assessmentData);

          if (existingResponse) {
            // Update existing record
            console.log('🔄 Updating existing assessment_responses record');
            const { data: updateResult, error: responseError } = await supabase
              .from('assessment_responses')
              .update(assessmentData)
              .eq('user_id', userId)
              .select();

            if (responseError) {
              console.error('❌ Error updating assessment responses:', responseError);
              alert(`Failed to update assessment responses: ${responseError.message}`);
            } else {
              console.log('✅ Assessment responses updated in database:', updateResult);
            }
          } else {
            // Insert new record
            console.log('➕ Inserting new assessment_responses record');
            const { data: insertResult, error: responseError } = await supabase
              .from('assessment_responses')
              .insert(assessmentData)
              .select();

            if (responseError) {
              console.error('❌ Error saving assessment responses:', responseError);
              alert(`Failed to save assessment responses: ${responseError.message}`);
            } else {
              console.log('✅ Assessment responses saved to database:', insertResult);
            }
          }
        } catch (responseError) {
          console.error('Error handling assessment responses:', responseError);
          // Don't throw - assessment was still saved to profile
        }
      }

      // Update wizard data using proper React state update
      updateData('finalAssessment', assessment);
      console.log('✅ Final assessment generated successfully', {
        assessmentType: typeof assessment,
        hasStrategicAdvantage: assessment?.strategicAdvantage ? 'yes' : 'no'
      });

    } catch (error) {
      console.error('Error generating final assessment:', error);
      alert(`Failed to generate assessment: ${error.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const loginAsAccount = async () => {
    if (!selectedAccount) {
      alert('No account selected');
      return;
    }

    setLoggingIn(true);
    try {
      // Store the selected account's auth user ID for the College context to use
      // Use auth_user_id if available, fallback to id for older accounts
      const userId = selectedAccount.auth_user_id || selectedAccount.id;
      localStorage.setItem('college-selected-student-id', userId);
      localStorage.setItem('college-test-mode', 'true');

      console.log('✅ Logged in as test account:', {
        id: userId,
        name: selectedAccount.name
      });

      // Redirect to college journey
      window.location.href = '/college';
    } catch (error) {
      console.error('Error logging in as account:', error);
      alert('Failed to login as account. Please try again.');
    } finally {
      setLoggingIn(false);
    }
  };

  const exportProfile = () => {
    const exportData = {
      selectedAccount: wizardData.selectedAccount,
      profile: wizardData.profile,
      parentInsights: wizardData.parentInsights,
      strategicContext: wizardData.strategicContext,
      questionSet: wizardData.questionSet,
      initialAnswers: wizardData.initialAnswers,
      followupQuestions: wizardData.followupQuestions,
      followupAnswers: wizardData.followupAnswers,
      finalAssessment: wizardData.finalAssessment,
      exportedAt: new Date().toISOString()
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `test-profile-${profile.name?.toLowerCase().replace(/\s+/g, '-') || 'student'}-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-slate-900 mb-2">Test Profile Complete!</h2>
        <p className="text-slate-600">
          Your comprehensive test profile for {profile.name} is ready. Review the summary below and export if needed.
        </p>
      </div>

      {/* Success Summary */}
      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-300 rounded-2xl p-8">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-white" />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-slate-900">Profile Successfully Created!</h3>
            <p className="text-slate-600">
              Complete test profile for {profile.name} is ready for testing
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg p-6 border border-emerald-200">
            <h4 className="font-semibold text-slate-900 mb-4">Profile Summary</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Student Name:</span>
                <span className="font-medium text-slate-900">{profile.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Graduation Year:</span>
                <span className="font-medium text-slate-900">{profile.graduation_year}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Intended Major:</span>
                <span className="font-medium text-slate-900">{profile.intended_major}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">GPA:</span>
                <span className="font-medium text-slate-900">{profile.gpa_weighted || profile.gpa_unweighted}</span>
              </div>
              {profile.sat_total && (
                <div className="flex justify-between">
                  <span className="text-slate-600">SAT:</span>
                  <span className="font-medium text-slate-900">{profile.sat_total}</span>
                </div>
              )}
              {profile.act_composite && (
                <div className="flex justify-between">
                  <span className="text-slate-600">ACT:</span>
                  <span className="font-medium text-slate-900">{profile.act_composite}</span>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 border border-emerald-200">
            <h4 className="font-semibold text-slate-900 mb-4">Data Generated</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Parent Insights:</span>
                <CheckCircle className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Strategic Context:</span>
                <CheckCircle className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Question Set (1-8):</span>
                <CheckCircle className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Initial Answers:</span>
                <CheckCircle className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Follow-up Questions (9-15):</span>
                <CheckCircle className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Follow-up Answers:</span>
                <CheckCircle className="w-4 h-4 text-emerald-600" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="grid md:grid-cols-2 gap-4">
        <button
          onClick={exportProfile}
          className="flex items-center justify-center gap-3 px-6 py-4 bg-azure-600 text-white rounded-lg hover:bg-azure-700 transition-colors font-semibold"
        >
          <Download className="w-5 h-5" />
          Export Test Profile
        </button>

        <button
          onClick={loginAsAccount}
          disabled={loggingIn}
          className="flex items-center justify-center gap-3 px-6 py-4 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-semibold disabled:opacity-50"
        >
          {loggingIn ? (
            <>
              <RefreshCw className="w-5 h-5 animate-spin" />
              Logging In...
            </>
          ) : (
            <>
              <LogIn className="w-5 h-5" />
              Login & Start Testing
            </>
          )}
        </button>
      </div>

      {/* Final Assessment Display */}
      {finalAssessment ? (
        <div className="space-y-6">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-emerald-900 mb-1">Strategic Assessment Generated!</h4>
              <p className="text-sm text-emerald-700">
                Review your comprehensive strategic assessment below. This is exactly what the student would see.
              </p>
            </div>
            <button
              onClick={generateFinalAssessment}
              disabled={generating}
              className="text-emerald-600 hover:text-emerald-800 flex items-center gap-2 text-sm font-medium disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
              {generating ? 'Regenerating...' : 'Regenerate'}
            </button>
          </div>

          {/* Render the assessment exactly like students see it */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-violet-600 to-purple-600 p-6">
              <h2 className="text-2xl font-bold text-white mb-2">
                Strategic Assessment for {profile.name}
              </h2>
              <p className="text-violet-100">
                Comprehensive college admissions strategy and positioning
              </p>
            </div>

            <div className="p-8">
              {typeof finalAssessment === 'object' && finalAssessment.strategicAdvantage ? (
                <StrategicResultsRenderer insights={finalAssessment} />
              ) : (
                <div className="prose prose-slate prose-lg max-w-none">
                  <pre className="whitespace-pre-wrap text-slate-700 bg-slate-50 p-4 rounded-lg">
                    {typeof finalAssessment === 'string' ? finalAssessment : JSON.stringify(finalAssessment, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-violet-50 border border-violet-200 rounded-lg p-6">
          <h4 className="font-semibold text-violet-900 mb-3">Optional: Generate Final Strategic Assessment</h4>
          <p className="text-sm text-violet-700 mb-4">
            You can also generate a complete strategic assessment with college recommendations now, or do it later through the normal flow.
          </p>
          <button
            onClick={generateFinalAssessment}
            disabled={generating}
            className="flex items-center gap-3 px-6 py-3 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors font-medium disabled:opacity-50"
          >
            {generating ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Generating Final Assessment...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Generate Final Assessment Now
              </>
            )}
          </button>
        </div>
      )}

      {/* Next Steps */}
      <div className="bg-azure-50 border border-azure-200 rounded-lg p-6">
        <h4 className="font-semibold text-azure-900 mb-3">Next Steps</h4>
        <ul className="space-y-2 text-sm text-azure-700">
          <li className="flex items-start gap-2">
            <span className="text-azure-600 mt-0.5">•</span>
            <span>All test data has been saved to <strong>{selectedAccount?.name  || 'the selected account'}</strong></span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-azure-600 mt-0.5">•</span>
            <span>Click "Login & Start Testing" to access the College Journey as this student</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-azure-600 mt-0.5">•</span>
            <span>Test college recommendations, essay guidance, and timeline features with realistic data</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-azure-600 mt-0.5">•</span>
            <span>Export the profile JSON to share or backup this test data</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-azure-600 mt-0.5">•</span>
            <span>All interactions will behave as if you're a real student going through the full journey</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default Step7_FinalAssessmentPreview;
